import { Alert, CircularProgress, Collapse, IconButton, Snackbar, Stack, LinearProgress, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo, useRef, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { shallow } from 'zustand/shallow';
import React from 'react';

import { getUrlParams } from '../utils/Seismic3DUrlUtils';
import { useGetGeomByVolumeToken, useGetSeismicDataByVolumeToken} from '../api/useSeismic3DInfoController';
import { useGetGeomByLayerAndFeature } from './projections/api/use3DFeatureInfoController';
import './Seismic3DPanel.css';
import { Seismic3DData } from '../models/classes/Seismic3DData';
import { MeshData } from '../models/types/MeshData';
import { use3DViewerStore } from '../stores/use3DViewerStore';
import { use3DGridStore } from '../stores/use3DGridStore';
import { Seismic3DNavbar } from './Seismic3DNavbar/Seismic3DNavbar';
import { getCentroidByWKT } from '../utils/OpenLayersUtils';
import { use3DSceneStore } from '../stores/use3DSceneStore';
import { GeopostSceneContainer } from './GeopostSceneContainer';
import { Grid3D } from './Grid3D';
import { useGetGridByToken } from '../api/useSeismic3DGridController';
import { GridXyz3D } from './GridXyz3D';
import { getCountTimeDepth } from '../threejs/utils/ScaleUtils';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';
import { SampleIntervalUnit } from 'features/seismic/models/enums/SampleIntervalUnity';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { VolumeType } from '../models/enums/VolumeType';
import { GeopostMuiPopup } from 'components/geopostMuiPopup/GeopostMuiPopup';
import { useSessionStore } from 'session/useSessionStore';
import { GeopostThreeJsConstants } from '../threejs/utils/GeopostThreeJsConstants';
import { IMetadata3DViewerRequest } from '../models/interfaces/requests/IMetadata3DViewerRequest';
import { useMetadata3DViewer } from '../api/useVolume';
import { Lines3DLegend } from './Lines3DLegend';
import { ISurveyMetadata } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { Viewer3DStrategy } from '../models/enums/Viewer3DStrategy';
import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { Position } from 'components/geopostPopup/models/enums/Position';

function FeatureViewer3DPanel(){

    //const [ isLoading, setIsLoading ] = useState(true);
    const [ loadingProgress, setLoadingProgress ] = useState(0);
    const [ isLoading, setIsLoading ] = useState(true);

    const [displayGridError, setDisplayGridError] = useState<boolean>(false);

    const [mainFeatureGridToken, setMainFeatureGridToken] = useState<string>();

    const { selectedVolumes, addSelectedVolume, setSeismic3DInfo } = use3DGridStore(state => ({
        selectedVolumes: state.selectedVolumes,
        addSelectedVolume: state.addSelectedVolume,
        setSeismic3DInfo: state.setSelectedSeismic3DInfo,
    }));

    const tenantConfig = useSessionStore(state => state.tenantConfiguration);

    const [ showAuthenticationError, setShowAuthenticationError ] = useState(false);

    const [canvasHeight, setCanvasHeight] = useState<number>(window.innerHeight);
    const [canvasWidth, setCanvasWidth] = useState<number>(window.innerWidth);
    const [ volumeIdentifier, setVolumeIdentifier ] = useState<string>();

    const { current: urlParams } = useRef(getUrlParams());

    const { current: layerId } = useRef(parseInt(urlParams.layerId));
    const { current: featureId }= useRef(parseInt(urlParams.featureId));
    const { current: featureType } = useRef(urlParams.type);
    const mainFeatureGeomSrid = useRef(3857);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const loadingAlertRef = useRef<HTMLButtonElement  | null>(null);
    const [ showLinesLegend, setShowLinesLegend ] = useState(false);

    //const [selectedLayers, setSelectedLayers] = useState<IntersectingLayer[]>([]);
    const [isIntersectingLayersLoading, setIsIntersectingLayersLoading] = useState<boolean>(false);

    const geopostScene = use3DSceneStore(state => state.scene);

    const { mainFeatureVolumeToken, setMainFeatureVolumeToken, setSampleInterval, setMainFeatureVolumeIdentifier, domain, setDomain } = use3DViewerStore(state => ({
        mainFeatureVolumeToken: state.mainFeatureVolumeToken,
        setMainFeatureVolumeToken: state.setMainFeatureVolumeToken,
        setSampleInterval: state.setSampleInterval,
        setMainFeatureVolumeIdentifier: state.setMainFeatureVolumeIdentifier,
        domain: state.domain,
        setDomain: state.setDomain,
    }), shallow);

    const { setMainFeatureCentroidX, setMainFeatureCentroidY, setCountTimeDepth, setHeightPixelFactor } = use3DViewerStore(state =>
        ({
            setMainFeatureCentroidX: state.setFeatureCentroidX,
            setMainFeatureCentroidY: state.setFeatureCentroidY,
            setCountTimeDepth: state.setCountTimeDepth,
            setHeightPixelFactor: state.setHeightPixelFactor,
            errorMessage: state.errorMessage,
            setErrorMessage: state.setErrorMessage,
        }), shallow);

    useEffect(() => {
        const type = urlParams.type;
        if (type === 'volume' && !!tenantConfig) {
            const volumeToken = urlParams.volumeToken;
            let volumeId = urlParams.volumeId;
            let volumeIdentifier = 'volumeToken';
            if (!!volumeToken) {
                volumeIdentifier = 'volumeToken';
            }
            else {
                volumeIdentifier = 'volumeId';
            }
            setMainFeatureVolumeToken(volumeToken ?? volumeId?.toString());
            setVolumeIdentifier(volumeIdentifier);
            setMainFeatureVolumeIdentifier(volumeIdentifier);
            getVolumeMetadata({
                volumeToken: volumeToken ?? volumeId!.toString(),
                screenHeight: window.screen.availHeight,
                tileWidth: 100,
                factorDivide: 1,
                factorToChangeDivide: 1,
                includePercentageCalc: true,
                skipInline: 0,
                skipXLine: 0,
                volumeIdentifier: volumeIdentifier,
            }).then(metadataReponse => {
                const surveyMetadata = metadataReponse.Metadata;
                addSelectedVolume(surveyMetadata);
                const totalLoadingIterationsParam = urlParams.totalLoadingIterations;
                if (totalLoadingIterationsParam > 0) {
                    setTotalGridLoadingIterations(totalLoadingIterationsParam);
                }
                setSeismic3DInfo(surveyMetadata.Survey3DInfo);
                setCountTimeDepth(getCountTimeDepth(surveyMetadata.Header.SampleInterval, surveyMetadata.Header.SampleIntervalUnit, surveyMetadata.Header.SamplesPerTrace));
                setHeightPixelFactor(surveyMetadata.Header.SamplesPerTrace);
                setSampleInterval(surveyMetadata.Header.SampleIntervalUnit === SampleIntervalUnit.Microsecond ? surveyMetadata.Header.SampleInterval / 1000 : surveyMetadata.Header.SampleInterval);
            }).catch(error => {
                setErrorMessage('An error occured during the volume data retrieval.');
            });
        }
        else if (type === 'grid') {
            setMainFeatureGridToken(urlParams.gridToken);
        }

    }, [tenantConfig]);

    /*useEffect(() => {
        if (metadata3DSeismicError) {
            setDisplayGridError(metadata3DSeismicError);
        }
    }, [metadata3DSeismicError]);*/

    const {data: mainFeatureLayerGeomData, refetch: fetchGeomByLayerAndFeature, error: layerError} = useGetGeomByLayerAndFeature(layerId, featureId,
        featureType, mainFeatureGeomSrid.current);

    const { data: mainFeatureGridData, error: gridError } = useGetGridByToken(mainFeatureGridToken, mainFeatureGeomSrid.current);

    //const previousIntersectingSeismics = useRef<IntersectingShape<Seismic3DData>[]>([]);
    //const previousIntersectingLayers = useRef<IntersectingShape<IntersectingLayer>[]>([]);

    //const { data: mainFeatureSeismicData, error: seismicDataError } = useGetSeismicDataByVolumeToken(mainFeatureVolumeToken);
    const [ volumeMetadataRequest, setVolumeMetadataRequest ] = useState<IMetadata3DViewerRequest>();
    const { mutateAsync: getVolumeMetadata } = useMetadata3DViewer();
    //[MERGE]
    /*const isSeismicSelected = (seismicData : Seismic3DData) =>
        selectedSeismics.find((seismic) => seismic.volumeToken === seismicData.volumeToken) !== undefined ? true : false;
    */

    //const { data: mainFeatureSeismicData, error: seismicDataError } = useGetSeismicDataByVolumeToken(mainFeatureVolumeToken ?? volumeId?.toString(), volumeIdentifier);
    const { data: mainFeatureSeismicGeomData } = useGetGeomByVolumeToken(tenantConfig?.viewer3DStrategy === Viewer3DStrategy.GeomBased ? mainFeatureVolumeToken : undefined,  volumeIdentifier ?? 'volumeToken');

    const volumes3D = useMemo(() => selectedVolumes.filter(volume => volume.Type === SurveyType.Seismic3D), [selectedVolumes]);

    const mainFeatureGeomData = use3DViewerStore(state => state.mainFeatureGeomData);
    const setMainFeatureGeomData = use3DViewerStore(state => state.setMainFeatureGeomData);

    const setErrorMessage = use3DViewerStore(state => state.setErrorMessage);

    const currentSelectedVolumesRef = useRef<ISurveyMetadata[]>([]);

    useEffect(() => {
        if (mainFeatureGeomData) {
            const [ mainFeatureCentroidX, mainFeatureCentroidY ] = getCentroidByWKT(mainFeatureGeomData?.CentroidWKT);
            setMainFeatureCentroidX(mainFeatureCentroidX);
            setMainFeatureCentroidY(mainFeatureCentroidY);
            if (geopostScene) {
                geopostScene.mainFeatureCentroid = [mainFeatureCentroidX, mainFeatureCentroidY];
            }
        }
    }, [mainFeatureGeomData]);

    const setTotalGridLoadingIterations = use3DGridStore(state => state.setTotalLoadingIterations);
    //const connectionError = useMemo(() => seismicDataError as AxiosError | undefined ?? gridError as AxiosError | undefined ?? layerError  as AxiosError | undefined, [seismicDataError, gridError, layerError]);

    useEffect(() => {
        setMainFeatureGeomData(mainFeatureLayerGeomData ?? mainFeatureSeismicGeomData);
    }, [mainFeatureLayerGeomData, mainFeatureSeismicGeomData]);

    //[MERGE]
    /*useEffect(() => {
        if (mainFeatureSeismicData) {
            setGridSelectedSeismic(mainFeatureSeismicData);
            setCountTimeDepth(getCountTimeDepth(mainFeatureSeismicData.sampleInterval, mainFeatureSeismicData.sampleIntervalUnit, mainFeatureSeismicData.samplesPerTrace));
            setHeightPixelFactor(mainFeatureSeismicData.samplesPerTrace);
            if (!mainFeatureVolumeToken) {
                setMainFeatureVolumeToken(mainFeatureSeismicData.volumeToken);
            }
        }
    }, [mainFeatureSeismicData]);
    */

    const errorMessage = use3DViewerStore(state => state.errorMessage);

    /*useEffect(() => {
        if (mainFeatureGeomData) {
            if (!mainFeatureGridData) {
                const meshData : MeshData = {
                    geomWKT: mainFeatureGeomData.GeomWKT,
                    centroidWKT: mainFeatureGeomData.CentroidWKT,
                    meshColor: '#0352ff',
                    meshToken: 'main-feature'
                };
                const mesh = geopostScene?.addMainMesh(meshData);
                geopostScene?.add(mesh!);
            }
        }
    }, [mainFeatureGeomData, geopostScene]);*/

    const sceneContainerDivRef = useRef<HTMLDivElement>(null);

    const updateWindowDimensions = () => {
        setCanvasHeight(window.innerHeight);
        setCanvasWidth(window.innerWidth);
    };

    const [ sliderSelectedVolumeToken, setSliderSelectedVolumeToken ] = useState<string|null>(null);

    const [ sliderOpened, setSliderOpened ] = useState(true);

    useEffect(() => {
        window.addEventListener('resize', updateWindowDimensions);
    }, []);

    useEffect(() => {
        if (mainFeatureGridData) {
            setMainFeatureCentroidX(mainFeatureGridData.CentroidX);
            setMainFeatureCentroidY(mainFeatureGridData.CentroidY);
            if (geopostScene) {
                geopostScene.mainFeatureCentroid = [mainFeatureGridData.CentroidX, mainFeatureGridData.CentroidY];
            }
            const sampleInterval = Math.ceil(Math.abs(mainFeatureGridData.Max - mainFeatureGridData.Min));
            setCountTimeDepth(sampleInterval);
            setSampleInterval(sampleInterval / 1000);
            setHeightPixelFactor(1000);
            setMainFeatureGeomData({
                GeomWKT: mainFeatureGridData.GeomWkt,
                CentroidWKT: mainFeatureGridData.CentroidWkt
            });
        }
    }, [mainFeatureGridData]);

    useEffect(() => {
        if (!!errorMessage) {
            setTimeout(() => setErrorMessage(null), 5000);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (selectedVolumes.length > 0 && !sliderSelectedVolumeToken) {
            setSliderSelectedVolumeToken(selectedVolumes[0].VolumeToken);
        }
    }, [selectedVolumes, sliderSelectedVolumeToken]);

    useEffect(() => {
        if (!domain && selectedVolumes.length > 0) {
            setDomain(selectedVolumes[0].Domain);
        }
    }, [selectedVolumes]);

    useEffect(() => {
        const removedVolumes = currentSelectedVolumesRef.current.filter(vol => !selectedVolumes.includes(vol));
        removedVolumes.forEach(vol => {
            geopostScene?.removeGrid(vol.VolumeToken);
        });
        currentSelectedVolumesRef.current = [...selectedVolumes];
    }, [selectedVolumes]);

    return (
        <React.Fragment>
            <Seismic3DNavbar onToggleSlider={() => setSliderOpened(!sliderOpened)}/>
            {
                true
                &&
                <React.Fragment>
                    {
                        mainFeatureGridData
                        &&
                        <GridXyz3D
                            onLoadStart={() => setLoadingProgress(50)}
                            onLoadEnd={() => setLoadingProgress(100)}
                            grid={mainFeatureGridData}
                            mainFeatureSrid={mainFeatureGeomSrid.current}
                            opacity={1}
                            visible={true}
                            scale={1}
                        />
                    }

                </React.Fragment>
            }
            <Collapse
                sx={{position: 'absolute', top: '10vh', left:'40vw', zIndex: 9999}}
                in={displayGridError}
            >
                <Alert
                    severity='error'
                    action={
                        <IconButton
                            aria-label='close'
                            color='inherit'
                            size='small'
                            onClick={
                                () => setDisplayGridError(false)
                            }
                        >
                            <CloseIcon fontSize='inherit'/>
                        </IconButton>
                    }
                >
                    <strong> Can not load seismic data for the selected volume. </strong>
                </Alert>
            </Collapse>
            <Stack direction='row' spacing={5}>
                <Stack width={canvasWidth}>
                    <div id='seismic-3d-canvas-container' ref={sceneContainerDivRef} style={{width: canvasWidth}}>
                        <GeopostSceneContainer height={canvasHeight - 64} width={canvasWidth}/>
                    </div>
                </Stack>
            </Stack>
            <Snackbar open={loadingProgress < 100} anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}>
                <Alert
                    severity='info'
                    variant='filled'
                    icon={<IconButton sx={{ color:'white', margin: 0, padding: 0 }} onClick={() => setShowLinesLegend(true)} ref={loadingAlertRef}><InfoOutlinedIcon/></IconButton>}
                >
                    <Stack gap={2}>
                        {
                            loadingProgress > 0 ?
                                <React.Fragment>
                                    <LinearProgress variant="determinate" value={loadingProgress} />
                                    <Typography>Loading slices...</Typography>
                                </React.Fragment>
                                :
                                <React.Fragment>
                                    <LinearProgress variant="indeterminate"/>
                                    <Typography>Loading initial content.</Typography>
                                </React.Fragment>
                        }
                    </Stack>
                </Alert>
            </Snackbar>
            <Lines3DLegend opened={showLinesLegend} onClose={() => setShowLinesLegend(false)} anchor={loadingAlertRef.current} anchorOrigin={{horizontal: 'right', vertical: 'top'}}/>
            <Snackbar open={!!errorMessage} anchorOrigin={{horizontal: 'center', vertical: 'top'}}>
                <Alert
                    variant='filled'
                    severity='error'
                >
                    {errorMessage}
                </Alert>
            </Snackbar>

            <GeopostPopup
                rebuildOnOpen={false}
                open={sliderOpened}
                onChangeOpen={setSliderOpened}
                width={'280px'}
                title={'3D Navigator'}
                position={Position.bottomRight}
                height={'350px'}
                temporaryIgnoreFlexInContainer={false}
                overlay={false}
            >
                <Stack gap={1} padding={'15px'} id='seismic-3d-lines-popup'>
                    {
                        selectedVolumes.length > 1 &&
                        <FormControl>
                            <InputLabel id='volume-slider-input-label'>Volume</InputLabel>
                            <Select
                                label="Volume"
                                labelId='volume-slider-input-label'
                                value={sliderSelectedVolumeToken}
                                onChange={(event) => setSliderSelectedVolumeToken(event.target.value)}
                            >
                                {
                                    volumes3D.map(volume =>
                                        <MenuItem key={volume.VolumeToken} value={volume.VolumeToken}>{volume.DisplayName}</MenuItem>
                                    )
                                }
                            </Select>
                        </FormControl>
                    }
                    {
                        <React.Fragment>
                            {
                                selectedVolumes.map((volume:ISurveyMetadata) =>
                                    <Grid3D
                                        isSliderVisible={sliderSelectedVolumeToken === volume.VolumeToken}
                                        key={volume.VolumeToken}
                                        onLoadStart={() => { } }
                                        onLoadEnd={() => setIsLoading(false)}
                                        volumeMetadata={volume}
                                        volumeIdentifier={volumeIdentifier ?? ''}
                                        onLoadingProgressChange={setLoadingProgress}
                                    />
                                )
                            }
                        </React.Fragment>
                    }
                </Stack>
            </GeopostPopup>
        </React.Fragment>
    );
};

export {FeatureViewer3DPanel};